import React from "react";
import { motion } from "framer-motion";

/**
 * Scroll-reveal wrapper. Fades + slides children in when they enter the viewport.
 * Respects prefers-reduced-motion automatically via framer-motion.
 *
 * Props:
 *  - as: element/component to render (default motion.div)
 *  - y / x: starting offset
 *  - delay, duration
 *  - amount: viewport threshold
 *  - once: animate only first time (default true)
 */
export default function Reveal({
  children,
  className,
  y = 32,
  x = 0,
  delay = 0,
  duration = 0.7,
  amount = 0.25,
  once = true,
  ...rest
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, x }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * Staggered container — children using <RevealItem> animate in sequence.
 */
export function RevealGroup({ children, className, stagger = 0.12, amount = 0.2, ...rest }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function RevealItem({ children, className, y = 28, ...rest }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
